import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-named-as-default
import apiService from '../../api/api';

const POLLING_INTERVAL = 30000; // 30 seconds
const TIMEOUT_DURATION = 300000; // 5 minutes

function PendingTimer({
  updatePollingFn, transactionId, handleUpdateStatus, orgId,
}) {
  const [progress, setProgress] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      try {
        // Polls from the post-payment page, where the student has already been
        // charged - so it reads the ungated /status route, not the bill. The
        // page itself is outside the buyer gate for that reason, and a poll
        // against the gated route would be refused for every buyer without a
        // session the moment the gate enforces: the student would watch a timer
        // that can never resolve, on a payment that had already gone through.
        //
        // skipAuthHandler stays belt-and-braces: a 401 from anywhere must not
        // replace this page with a login form mid-poll.
        const response = await apiService.get(
          `view/pre-payment/${transactionId}/status`,
          {},
          { organization: orgId },
          { skipAuthHandler: true },
        );
        const invoiceStatus = response?.prePaymentInvoice?.invoiceStatus;

        if (['paid', 'cancelled'].includes(invoiceStatus)) {
          let changedStatus = 'success';
          if (invoiceStatus === 'cancelled') {
            changedStatus = 'failure';
          }
          handleUpdateStatus(changedStatus);
          clearInterval(intervalRef.current);
          updatePollingFn(true);
        }
      } catch (err) {
        updatePollingFn(true);
      }
    };

    const startPolling = () => {
      intervalRef.current = setInterval(() => {
        fetchPaymentStatus();
      }, POLLING_INTERVAL);
    };

    const startTimer = () => {
      timerRef.current = setInterval(() => {
        setTimeElapsed((prev) => {
          const newTime = prev + 1000; // Increase every second
          setProgress((newTime / TIMEOUT_DURATION) * 100);
          return newTime;
        });
      }, 1000);
    };

    startPolling();
    startTimer();

    timeoutRef.current = setTimeout(() => {
      clearInterval(intervalRef.current);
      clearInterval(timerRef.current);
    }, TIMEOUT_DURATION);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(timerRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, [transactionId]);

  return (
    <div className="w-full z-100 max-w-md p-4  rounded-lg bg-white">
      {timeElapsed < TIMEOUT_DURATION ? (
        <>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Time elapsed:
            {Math.floor(timeElapsed / 1000)}
            s
          </p>
        </>
      ) : (
        <p className="text-red-600 text-lg font-semibold">Please check after some time.</p>
      )}
    </div>
  );
}

PendingTimer.propTypes = {
  updatePollingFn: PropTypes.func.isRequired,
  transactionId: PropTypes.string.isRequired,
  handleUpdateStatus: PropTypes.func.isRequired,
  orgId: PropTypes.string,
};

PendingTimer.defaultProps = {
  orgId: undefined,
};

export default PendingTimer;
